/**
 * What the last crawl and audit actually did.
 *
 * "completed · 1h ago" says a job ran, not what it found. Pages failed is the
 * number that matters most here and was not shown at all: a crawl that drops
 * most of a site still reports success, and this is where that would show.
 */

import type { ReactNode } from 'react';

import { Badge, Card, StatusBadge } from '@/components/ui';
import { CheckCircleIcon, ClockIcon, RefreshIcon } from '@/components/icons';
import { cn, formatDuration, formatNumber, formatRelative, plural } from '@/utils/format';
import type { DashboardResponse } from '@/types/api';

export function ActivityCard({
  crawl,
  audit,
}: {
  crawl: DashboardResponse['last_crawl'];
  audit: DashboardResponse['last_audit'];
}) {
  return (
    <Card padded={false}>
      <div className="border-b border-border-subtle px-5 py-3.5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
          <ClockIcon className="h-4 w-4 text-ink-subtle" />
          Latest activity
        </h2>
      </div>

      <RunBlock
        icon={<RefreshIcon className="h-4 w-4" />}
        label="Crawl"
        status={crawl?.status}
        at={crawl?.finished_at ?? crawl?.created_at ?? null}
        duration={crawl?.duration_seconds ?? null}
        facts={
          crawl
            ? [
                { label: 'crawled', value: formatNumber(crawl.pages_crawled) },
                { label: 'discovered', value: formatNumber(crawl.pages_discovered) },
                {
                  label: 'failed',
                  value: formatNumber(crawl.pages_failed),
                  tone: crawl.pages_failed > 0 ? ('danger' as const) : undefined,
                },
              ]
            : []
        }
      />

      <RunBlock
        icon={<CheckCircleIcon className="h-4 w-4" />}
        label="Audit"
        status={audit?.status}
        at={audit?.finished_at ?? audit?.created_at ?? null}
        duration={audit?.duration_seconds ?? null}
        facts={
          audit
            ? [
                {
                  label: plural(audit.issues_found, 'issue'),
                  value: formatNumber(audit.issues_found),
                },
                {
                  label: plural(audit.opportunities_found, 'opportunity', 'opportunities'),
                  value: formatNumber(audit.opportunities_found),
                },
                {
                  label: `${plural(audit.pages_analyzed, 'page')} read`,
                  value: formatNumber(audit.pages_analyzed),
                },
              ]
            : []
        }
        flags={
          audit
            ? [
                audit.used_search_console ? 'Search Console' : null,
                audit.used_ai_reasoning ? 'AI' : null,
              ].filter((flag): flag is string => Boolean(flag))
            : []
        }
      />
    </Card>
  );
}

function RunBlock({
  icon,
  label,
  status,
  at,
  duration,
  facts,
  flags = [],
}: {
  icon: ReactNode;
  label: string;
  status: string | undefined;
  at: string | null;
  duration: number | null;
  facts: { label: string; value: string; tone?: 'danger' }[];
  flags?: string[];
}) {
  return (
    <div className="border-b border-border-subtle px-5 py-3.5 last:border-b-0">
      <div className="flex items-center gap-2">
        <span className="text-ink-subtle">{icon}</span>
        <span className="text-sm font-medium text-ink">{label}</span>
        <span className="ml-auto">
          {status ? (
            <StatusBadge status={status} />
          ) : (
            <span className="text-xs text-ink-subtle">never run</span>
          )}
        </span>
      </div>

      {facts.length > 0 ? (
        <dl className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1">
          {facts.map((fact) => (
            <div key={fact.label} className="flex items-baseline gap-1">
              <dd
                className={cn(
                  'tnum text-sm font-semibold',
                  fact.tone === 'danger' ? 'text-danger' : 'text-ink',
                )}
              >
                {fact.value}
              </dd>
              <dt className="text-[11px] text-ink-subtle">{fact.label}</dt>
            </div>
          ))}
        </dl>
      ) : null}

      {at ? (
        <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-ink-subtle">
          <span>{formatRelative(at)}</span>
          {duration !== null ? <span>· took {formatDuration(duration)}</span> : null}
          {flags.map((flag) => (
            <Badge key={flag} tone="brand">
              {flag}
            </Badge>
          ))}
        </p>
      ) : null}
    </div>
  );
}
